import React, { FC, ReactNode, useState } from 'react';
import { Grid, MenuItem, TextField } from '@mui/material';
import { NumericFormat } from 'react-number-format';
import MaskedTextInputFormik from 'components/MaskedTextInputFormik';
import { AddressSkeleton } from './AddressSkeleton';

export const statesList = [
  'AC',
  'AL',
  'AM',
  'AP',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MG',
  'MS',
  'MT',
  'PA',
  'PB',
  'PE',
  'PI',
  'PR',
  'RJ',
  'RN',
  'RO',
  'RR',
  'RS',
  'SC',
  'SE',
  'SP',
  'TO',
];

type Props = {
  values: {
    addressPostalCode: string;
    addressState: string;
    addressCity: string;
    addressNeighborhood: string;
    addressStreet: string;
    addressNumber: string;
    addressComplement: string;
  };
  setFieldValue: (field: string, value: any) => void;
  errors?: any;
  touched?: any;
  getFieldError?: (fieldName: any, touched: any, errors: any) => boolean;
  getFieldHelperText?: (fieldName: any, touched: any, errors: any) => string;
  enqueueSnackbar: (msg: ReactNode, options?: any) => void;
};

const AddressForm: FC<Props> = ({
  values,
  setFieldValue,
  errors,
  touched,
  getFieldError = () => false,
  getFieldHelperText = () => '',
  enqueueSnackbar,
}) => {
  const [loading, setLoading] = useState(false);

  const handleCepBlur = async () => {
    const cleanCep = String(values.addressPostalCode || '').replace(/\D/g, '');

    if (cleanCep.length !== 8) return;

    setLoading(true);

    try {
      const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);

      const data = await response.json();

      if (data.erro) {
        enqueueSnackbar('CEP não encontrado.', { variant: 'warning' });
        return;
      }
      setFieldValue('addressState', data.uf || '');
      setFieldValue('addressCity', data.localidade || '');
      setFieldValue('addressNeighborhood', data.bairro || '');
      setFieldValue('addressStreet', data.logradouro || '');
    } catch {
      enqueueSnackbar('Erro ao consultar CEP.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Grid item xs={12} sm={6}>
        <MaskedTextInputFormik
          mode="cep"
          name="addressPostalCode"
          label="CEP*"
          placeholder="000000-000"
          value={values.addressPostalCode}
          onChange={(e: any) => setFieldValue('addressPostalCode', e.target.value)}
          onBlur={handleCepBlur}
          error={getFieldError('addressPostalCode', touched, errors)}
          helperText={getFieldHelperText('addressPostalCode', touched, errors)}
          fullWidth
          disabled={loading}
        />
      </Grid>

      {loading ? (
        <AddressSkeleton />
      ) : (
        <>
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                variant="standard"
                name="addressState"
                required
                label="Estado"
                value={values.addressState}
                onChange={(e) => setFieldValue('addressState', e.target.value)}
                error={getFieldError('addressState', touched, errors)}
                helperText={getFieldHelperText('addressState', touched, errors)}
                fullWidth
              >
                <MenuItem value="">Selecionar</MenuItem>
                {statesList.map((uf) => (
                  <MenuItem key={uf} value={uf}>
                    {uf}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                variant="standard"
                name="addressCity"
                required
                label="Cidade"
                value={values.addressCity}
                placeholder="Selecione a cidade"
                onChange={(e) => setFieldValue('addressCity', e.target.value)}
                error={getFieldError('addressCity', touched, errors)}
                helperText={getFieldHelperText('addressCity', touched, errors)}
                fullWidth
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                variant="standard"
                name="addressNeighborhood"
                required
                label="Bairro"
                placeholder="Nome do bairro"
                value={values.addressNeighborhood}
                onChange={(e) => setFieldValue('addressNeighborhood', e.target.value)}
                error={getFieldError('addressNeighborhood', touched, errors)}
                helperText={getFieldHelperText('addressNeighborhood', touched, errors)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                variant="standard"
                name="addressStreet"
                required
                label="Logradouro"
                placeholder="Rua, avenida, etc."
                value={values.addressStreet}
                onChange={(e) => setFieldValue('addressStreet', e.target.value)}
                error={getFieldError('addressStreet', touched, errors)}
                helperText={getFieldHelperText('addressStreet', touched, errors)}
                fullWidth
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12} sm={6}>
              <NumericFormat
                customInput={TextField}
                variant="standard"
                name="addressNumber"
                required
                label="Número"
                placeholder="0000"
                value={values.addressNumber}
                onValueChange={(v) => setFieldValue('addressNumber', v.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                variant="standard"
                name="addressComplement"
                label="Complemento (opcional)"
                placeholder="Ex: Apartamento, casa"
                value={values.addressComplement}
                onChange={(e) => setFieldValue('addressComplement', e.target.value)}
                fullWidth
              />
            </Grid>
          </Grid>
        </>
      )}
    </>
  );
};

export default AddressForm;
